import { Injectable } from '@angular/core';
import type { FlexibleRuleDefinition } from '@domain/rule-definitions';
import {
	FilteredResult,
	RuleDefinitionsSelectors,
	RulesEngineService,
	RulesReducerService,
} from '@domain/rule-definitions';
import { MatterPackageContentType } from '@models/matter-package';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { createSelector } from '@ngxs/store';
import {
	getMappedMortgageName,
	getMortgageName,
} from 'src/app/shared/core/util/mortgage';
import { BusinessFeatureFlagSelectors } from 'src/app/shared/state/business/business-feature-flag.selectors';
import { CustomTags } from 'src/app/shared/state/global/tags/tags.models';
import { TagsSelector } from 'src/app/shared/state/global/tags/tags.selector';
import { MatterAllDocumentsAggregatorState } from 'src/app/shared/state/matter/matter-all-documents-aggregator/matter-all-documents-aggregator.state';
import { MatterWordDocumentWithMetadata } from 'src/app/shared/state/matter/matter-word-documents/matter-word-documents.selectors';
import { UserProfileState } from 'src/app/shared/state/user-profile';
import {
	DocumentsV2Selectors,
	KeywordLabeledFormTemplate,
	KeywordLabeledTemplate,
	MatterFileWithKeywords,
	MatterPackageWithStatusAndDocuments,
	UploadedVoidCheque,
} from '../documents-v2.selectors';

// Type for combined content from different sources
type CombinedContentItem =
	| KeywordLabeledTemplate
	| KeywordLabeledFormTemplate
	| MatterFileWithKeywords;

type PackageResult = {
	rule: FlexibleRuleDefinition;
	rootContext: Record<string, Record<string, unknown>>;
	results: Array<{
		content: CombinedContentItem;
		context: Record<string, Record<string, unknown>>;
	}>;
};

@Injectable({
	providedIn: 'root',
})
export class MatterPackagesSelectors {
	private static rulesReducerService = new RulesReducerService();
	private static rulesEngineService = new RulesEngineService();

	private static flattenFilteredResults<T>(
		results: Array<FilteredResult<T>>,
		isRoot = true,
	): {
		flattenedResults: Array<{
			content: T;
			context: Record<string, Record<string, unknown>>;
		}>;
		rootContext: Record<string, Record<string, unknown>>;
	} {
		const flattenedResults: Array<{
			content: T;
			context: Record<string, Record<string, unknown>>;
		}> = [];
		const rootContext: Record<string, Record<string, unknown>> = {};

		results.forEach((result) => {
			const currentContext: Record<string, Record<string, unknown>> = {};

			if (result.context) {
				Object.entries(result.context).forEach(([contextKey, contextValue]) => {
					if (
						contextValue &&
						typeof contextValue === 'object' &&
						contextValue !== null &&
						'id' in contextValue
					) {
						const contextType = contextKey.toLowerCase();
						const contextId = String((contextValue as { id: unknown }).id);

						if (!currentContext[contextType]) {
							currentContext[contextType] = {};
						}

						currentContext[contextType][contextId] = contextValue;

						if (isRoot) {
							if (!rootContext[contextType]) {
								rootContext[contextType] = {};
							}
							rootContext[contextType][contextId] = contextValue;
						}
					}
				});
			}

			if (result.content) {
				result.content.forEach((item) => {
					flattenedResults.push({
						content: item,
						context: currentContext,
					});
				});
			}

			if (result.children) {
				const childrenResult = this.flattenFilteredResults(
					result.children,
					false,
				);
				flattenedResults.push(...childrenResult.flattenedResults);

				Object.entries(childrenResult.rootContext).forEach(
					([contextType, contextData]) => {
						if (isRoot) {
							if (!rootContext[contextType]) {
								rootContext[contextType] = {};
							}
							Object.assign(rootContext[contextType], contextData);
						}
					},
				);
			}
		});

		return {
			flattenedResults,
			rootContext,
		};
	}

	static readonly combinedContent = createSelector(
		[
			DocumentsV2Selectors.wordTemplates,
			DocumentsV2Selectors.labeledFormTemplates,
			DocumentsV2Selectors.uploadedFilesKeywords,
			DocumentsV2Selectors.uploadedVoidCheques,
			BusinessFeatureFlagSelectors.isSuggestedPackagesEnabled,
		],
		(
			wordTemplates: KeywordLabeledTemplate[],
			labeledFormTemplates: KeywordLabeledFormTemplate[],
			uploadedFiles: MatterFileWithKeywords[],
			uploadedVoidCheques: UploadedVoidCheque[],
			isSuggestedPackagesEnabled: boolean,
		): CombinedContentItem[] => {
			if (!isSuggestedPackagesEnabled) {
				return [];
			}

			return [
				...(wordTemplates || []),
				...(labeledFormTemplates || []),
				...(uploadedFiles || []),
				...(uploadedVoidCheques || []),
			];
		},
	);

	static readonly relevantRules = createSelector(
		[
			RuleDefinitionsSelectors.getFilteredRuleDefinitionsByBusiness,
			TagsSelector.getTags,
			BusinessFeatureFlagSelectors.isSuggestedPackagesEnabled,
			UserProfileState.getCurrentBusinessId,
		],
		(
			getFilteredRules,
			contextData: CustomTags,
			isSuggestedPackagesEnabled: boolean,
			businessId: string,
		): FlexibleRuleDefinition[] => {
			if (!getFilteredRules || !contextData || !isSuggestedPackagesEnabled) {
				return [];
			}

			// Get rules filtered by current business + global rules
			const filteredRuleDefinitions = getFilteredRules(businessId);

			const masterRule = filteredRuleDefinitions.find((rule) =>
				rule.keywords?.includes('masterRule'),
			);

			if (!masterRule?.ruleContent) {
				return [];
			}

			return MatterPackagesSelectors.rulesReducerService.reduce(
				masterRule,
				contextData,
				filteredRuleDefinitions,
			);
		},
	);

	static readonly packageResults = createSelector(
		[
			MatterPackagesSelectors.relevantRules,
			TagsSelector.getTags,
			MatterPackagesSelectors.combinedContent,
			BusinessFeatureFlagSelectors.isSuggestedPackagesEnabled,
		],
		(
			relevantRules: FlexibleRuleDefinition[],
			contextData: CustomTags,
			combinedContent: CombinedContentItem[],
			isSuggestedPackagesEnabled: boolean,
		): PackageResult[] => {
			if (!relevantRules || !contextData || !isSuggestedPackagesEnabled) {
				return [];
			}

			const packageResults = relevantRules.reduce((acc, rule) => {
				const ruleResults = MatterPackagesSelectors.rulesEngineService.execute(
					rule,
					contextData,
					combinedContent,
				);

				const results = ruleResults.map((r) => {
					const { flattenedResults, rootContext } =
						MatterPackagesSelectors.flattenFilteredResults([r]);
					return {
						rule,
						rootContext,
						results: flattenedResults,
					};
				});

				return [...acc, ...results];
			}, []);

			return packageResults;
		},
	);

	static readonly getDynamicPackages = createSelector(
		[
			MatterAllDocumentsAggregatorState.getAllVirtualAndMatterDocuments,
			MatterPackagesSelectors.packageResults,
			BusinessFeatureFlagSelectors.isSuggestedPackagesEnabled,
		],
		(
			allDocuments: MatterWordDocumentWithMetadata[],
			packageResults: PackageResult[],
			isSuggestedPackagesEnabled: boolean,
		): MatterPackageWithStatusAndDocuments[] => {
			if (!allDocuments || !packageResults || !isSuggestedPackagesEnabled) {
				return [];
			}

			return packageResults
				.map((result: PackageResult, index: number) => {
					const documents = result.results
						.map((resultItem) => {
							const content = resultItem.content;
							const itemContext = resultItem.context;

							const doc = allDocuments.find((doc) => {
								let sourceMatches = doc.document.source === content.source;
								if (
									content.contentType ===
										MatterPackageContentType.UPLOADED_FILE ||
									content.contentType === MatterPackageContentType.FORMS
								) {
									sourceMatches = doc.document.source === content.id;
								}

								let contextMatches = !content.groupType;
								if (
									itemContext &&
									typeof itemContext === 'object' &&
									content.groupType
								) {
									const contextKeyMap: Record<string, string> = {
										client: 'clients',
										mortgage: 'mortgages',
										jurisdiction: 'jurisdictions',
										encumbrance: 'encumbrances',
										debt: 'debts',
										complianceRequest: 'complianceRequests',
									};

									Object.entries(itemContext).forEach(
										([contextType, contextData]) => {
											if (contextData && typeof contextData === 'object') {
												Object.entries(contextData).forEach(
													([contextId, contextValue]) => {
														if (
															contextValue &&
															typeof contextValue === 'object' &&
															doc.document.context
														) {
															const documentContextKey =
																contextKeyMap[contextType] || contextType;
															const docContextItems =
																doc.document.context[
																	documentContextKey as keyof typeof doc.document.context
																];

															if (Array.isArray(docContextItems)) {
																contextMatches =
																	contextMatches ||
																	docContextItems.some(
																		(item) =>
																			item.referenceId === String(contextId),
																	);
															}
														}
													},
												);
											}
										},
									);
								}

								return sourceMatches && contextMatches;
							});

							if (!doc) {
								return null;
							}

							return {
								...doc,
								contentType: content.contentType,
							};
						})
						.filter(
							(
								doc,
							): doc is MatterWordDocumentWithMetadata & {
								contentType: MatterPackageContentType;
							} => !!doc,
						);

					if (documents.length === 0) {
						return null;
					}

					const contextIds = Object.entries(result.rootContext)
						.flatMap(([contextType, contextData]) =>
							Object.keys(contextData).map(
								(contextId) => `${contextType}:${contextId}`,
							),
						)
						.sort()
						.join('-');

					const packageId = contextIds
						? `virtual-${result.rule.id}-${contextIds}`
						: `virtual-${result.rule.id}-${index}`;

					// const contextDescription =
					// 	MatterPackagesSelectors.getContextDescription(result.rootContext);
					// const packageName = contextDescription
					// 	? `${result.rule.name} | ${contextDescription}`
					// 	: result.rule.name;

					const pkg: MatterPackageWithStatusAndDocuments = {
						id: packageId,
						packageName: result.rule.name,
						packageTemplateId: result.rule.id,
						createdAt: Date.now(),
						createdBy: 'system',
						lastUpdatedAt: Date.now(),
						lastUpdatedBy: 'system',
						lastExportedAt: 0,
						lastExportedBy: '',
						history: [],
						contents: documents.map((doc, index) => ({
							id: doc.document.id,
							ordinal: index,
							contentType: doc.contentType,
						})),
						isExporting: false,
						documents,
						packageHash: packageId,
						syncMortgageId:
							(
								Object.values(
									result?.rootContext?.mortgage ?? {},
								)?.[0] as Record<string, unknown>
							)?.source === 'closerSync'
								? String(
										(
											Object.values(
												result?.rootContext?.mortgage ?? {},
											)?.[0] as Record<string, unknown>
										)?.id,
									)
								: null,
					};
					return pkg;
				})
				.filter((pkg) => pkg !== null) as MatterPackageWithStatusAndDocuments[];
		},
	);

	static readonly getAllPackagesWithStatusesAndDocuments = createSelector(
		[
			DocumentsV2Selectors.getPackagesWithStatusesAndDocuments,
			MatterPackagesSelectors.getDynamicPackages,
			BusinessFeatureFlagSelectors.isSuggestedPackagesEnabled,
		],
		(
			matterPackages: MatterPackageWithStatusAndDocuments[],
			virtualPackages: MatterPackageWithStatusAndDocuments[],
			isSuggestedPackagesEnabled: boolean,
		): {
			matterPackages: MatterPackageWithStatusAndDocuments[];
			allVirtualPackages: MatterPackageWithStatusAndDocuments[];
			displayVirtualPackages: MatterPackageWithStatusAndDocuments[];
		} => {
			const matterPackageHashes = new Set(
				matterPackages.map((pkg) => pkg.packageHash),
			);
			const displayVirtualPackages = virtualPackages.filter(
				(pkg) => !matterPackageHashes.has(pkg.packageHash),
			);

			const matterPackages_ = matterPackages.map((pkg) => {
				if (
					pkg.packageHash &&
					virtualPackages.find((vpkg) => vpkg.packageHash === pkg.packageHash)
				) {
					const virtualPkgMortgageId = virtualPackages.find(
						(vpkg) => vpkg.packageHash === pkg.packageHash,
					)?.syncMortgageId;
					return { ...pkg, syncMortgageId: virtualPkgMortgageId };
				}
				return pkg;
			});

			return {
				matterPackages: matterPackages_,
				allVirtualPackages: isSuggestedPackagesEnabled ? virtualPackages : [],
				displayVirtualPackages: isSuggestedPackagesEnabled
					? displayVirtualPackages
					: [],
			};
		},
	);

	static readonly getMatterPackageById = createSelector(
		[MatterPackagesSelectors.getAllPackagesWithStatusesAndDocuments],
		({ matterPackages }) =>
			(packageId: string) =>
				matterPackages.find((pkg) => pkg.id === packageId),
	);

	static readonly getVirtualPackages = createSelector(
		[MatterPackagesSelectors.getAllPackagesWithStatusesAndDocuments],
		(packageResults): MatterPackageWithStatusAndDocuments[] | undefined => {
			if (!packageResults) return undefined;
			const matterPackageHashes = new Set(
				packageResults.matterPackages.map((pkg) => pkg.packageHash),
			);
			return packageResults.allVirtualPackages.filter(
				(pkg) => !matterPackageHashes.has(pkg.packageHash),
			);
		},
	);

	private static getContextDescription(
		context: Record<string, Record<string, unknown>>,
	): string {
		if (!context || Object.keys(context).length === 0) {
			return '';
		}

		return Object.entries(context)
			.map(([key, valueMap]) => {
				// valueMap is a Record<string, unknown> where keys are IDs
				// Get all the actual objects from the map
				const contextObjects = Object.values(valueMap);

				// For now, just use the first object (typically there's only one per context type)
				if (contextObjects.length === 0) {
					return '';
				}

				const obj = contextObjects[0] as Record<string, unknown>;
				let displayValue: string;

				if (key === 'mortgage' || key === 'mortgages') {
					displayValue = this.getMortgageDisplayName(obj);
				} else if (key === 'client' || key === 'clients') {
					displayValue = this.getDisplayValue(obj);
				} else {
					displayValue = this.getDisplayValue(obj);
				}

				return displayValue;
			})
			.filter((val) => val !== '')
			.join(' | ');
	}

	private static getMortgageDisplayName(obj: Record<string, unknown>): string {
		// Check if this is a MappedMortgage (from tags) or MatterMortgageWithAssociations
		const isMappedMortgage =
			obj.lenders && typeof obj.lenders === 'object' && 'all' in obj.lenders;

		if (isMappedMortgage) {
			// Handle MappedMortgage format from tags
			return getMappedMortgageName(obj);
		} else {
			// Handle MatterMortgageWithAssociations format
			if (obj.priority || obj.lenders || obj.transactionType || obj.amount) {
				return getMortgageName(
					obj as unknown as MatterMortgageWithAssociations,
				);
			}
		}

		return this.getDisplayValue(obj);
	}

	private static getDisplayValue(value: unknown): string {
		if (value === null || value === undefined) {
			return '';
		}

		if (
			typeof value === 'string' ||
			typeof value === 'number' ||
			typeof value === 'boolean'
		) {
			return String(value);
		}

		if (typeof value === 'object') {
			const obj = value as Record<string, unknown>;

			if (obj.name && typeof obj.name === 'string') return obj.name;
			if (obj.formatted && typeof obj.formatted === 'string')
				return obj.formatted;
			if (obj.defaultValue !== undefined)
				return this.getDisplayValue(obj.defaultValue);

			if (Array.isArray(value)) {
				return `${value.length} item${value.length !== 1 ? 's' : ''}`;
			}

			const stringProps = Object.values(obj).filter(
				(v) => typeof v === 'string' && v.length > 0,
			);
			if (stringProps.length > 0) return stringProps[0] as string;

			return '[Complex Object]';
		}

		return String(value);
	}
}
